/**
 * Shared AudioContext for the voice-processing graphs (the publisher vocal
 * chain in services/voiceChain.js and the per-user remote graphs in
 * services/remoteAudio.js). One context, created lazily, pinned to 48 kHz (the
 * Opus/SFU rate — avoids a resample on both sides) with an interactive
 * latency hint.
 *
 * Deliberately distinct from notifySound.js's context: notification blips are
 * fire-and-forget at the device default rate; the voice graphs want a pinned
 * rate and a long-lived context whose suspended/running state we manage.
 *
 * Autoplay policy: a context created outside a user gesture starts
 * 'suspended'. Graph owners call resumeAudioContext() when they attach
 * (publish/watch are click-driven, so this almost always runs inside a
 * gesture); as a fallback, the first pointer/key gesture after a suspended
 * creation retries the resume and unhooks itself once running.
 */

let sharedCtx = null;

function hookGestureResume() {
  if (typeof document === 'undefined') return;
  const tryResume = () => {
    if (sharedCtx && sharedCtx.state === 'suspended' && sharedCtx.resume) {
      sharedCtx.resume().catch(() => {});
    }
    if (!sharedCtx || sharedCtx.state !== 'suspended') {
      document.removeEventListener('pointerdown', tryResume, true);
      document.removeEventListener('keydown', tryResume, true);
    }
  };
  document.addEventListener('pointerdown', tryResume, true);
  document.addEventListener('keydown', tryResume, true);
}

/**
 * Get (lazily creating) the shared voice AudioContext. Returns null when the
 * environment has no Web Audio at all — callers treat that as "feature off"
 * and fall back to raw passthrough.
 */
export function getSharedAudioContext() {
  if (typeof window === 'undefined') return null;
  const Ctx = window.AudioContext || window.webkitAudioContext;
  if (!Ctx) return null;
  if (!sharedCtx) {
    try {
      sharedCtx = new Ctx({ sampleRate: 48000, latencyHint: 'interactive' });
    } catch (e) {
      // Some implementations reject the sampleRate option — a default-rate
      // context still works (the browser resamples), better than no feature.
      try {
        sharedCtx = new Ctx();
      } catch (e2) {
        return null;
      }
    }
    if (sharedCtx.state === 'suspended') hookGestureResume();
  }
  return sharedCtx;
}

/** Best-effort resume; safe to call from anywhere, never throws. */
export function resumeAudioContext() {
  const ctx = getSharedAudioContext();
  if (ctx && ctx.state === 'suspended' && ctx.resume) {
    ctx.resume().catch(() => {});
  }
}
